import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, CheckCircle, Clock, AlertCircle, X, Paperclip, HelpCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const SupportTicketsPanel = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [helpButtonVisible, setHelpButtonVisible] = useState(true);

  useEffect(() => {
    fetchTickets();
    // Load help button visibility setting
    const visible = localStorage.getItem('helpButtonVisible');
    setHelpButtonVisible(visible !== 'false');
  }, []);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/support/tickets/my_tickets/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = async (ticket) => {
    setSelectedTicket(ticket);

    // Mark ticket as viewed to clear notifications
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/api/support/tickets/${ticket.id}/mark_as_viewed/`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      console.log(`✅ Ticket #${ticket.id} marked as viewed`);
    } catch (error) {
      console.error('Error marking ticket as viewed:', error);
    }
  };

  const handleSendReply = async (ticketId) => {
    if (!replyMessage.trim()) return;

    setSendingReply(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/api/support/tickets/${ticketId}/add_response/`,
        { message: replyMessage },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setReplyMessage('');
      fetchTickets(); // Refresh tickets to show new response

      // Update selected ticket
      const updatedResponse = await axios.get(`${API_BASE_URL}/api/support/tickets/${ticketId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSelectedTicket(updatedResponse.data);
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertCircle className="h-4 w-4" />,
      in_progress: <Clock className="h-4 w-4" />,
      resolved: <CheckCircle className="h-4 w-4" />,
      closed: <CheckCircle className="h-4 w-4" />
    };
    return icons[status] || <MessageCircle className="h-4 w-4" />;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-green-600',
      medium: 'text-yellow-600',
      high: 'text-orange-600',
      critical: 'text-red-600'
    };
    return colors[priority] || 'text-gray-600';
  };

  const toggleHelpButton = () => {
    const newVisibility = !helpButtonVisible;
    setHelpButtonVisible(newVisibility);
    localStorage.setItem('helpButtonVisible', String(newVisibility));
    // Trigger a storage event to update the HelpButton component
    window.dispatchEvent(new Event('storage'));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Support Tickets</h2>
          <p className="text-gray-600 mt-1">View and manage your support requests</p>
        </div>

        {/* Help Button Toggle */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <HelpCircle className="h-5 w-5 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Help Button</p>
              <p className="text-xs text-gray-500">Show floating help button</p>
            </div>
            <button
              onClick={toggleHelpButton}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                helpButtonVisible ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  helpButtonVisible ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {!helpButtonVisible && (
            <p className="text-xs text-orange-600 mt-2">
              ⚠️ Help button is hidden. Enable it to quickly submit tickets.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tickets List */}
        <div className="lg:col-span-1 space-y-3">
          {tickets.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No support tickets yet</p>
              <p className="text-sm text-gray-400 mt-1">Click the Help button to create one</p>
            </div>
          ) : (
            tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => handleSelectTicket(ticket)}
                className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                  selectedTicket?.id === ticket.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-blue-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-gray-900 text-sm line-clamp-1">
                    {ticket.subject}
                  </h3>
                  <span className={`ml-2 ${getPriorityColor(ticket.priority)}`}>
                    •
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                    {getStatusIcon(ticket.status)}
                    {ticket.status_display}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </span>
                </div>
                {ticket.responses && ticket.responses.length > 0 && (
                  <div className="mt-2 text-xs text-gray-500 flex items-center">
                    <MessageCircle className="h-3 w-3 mr-1" />
                    {ticket.responses.length} {ticket.responses.length === 1 ? 'reply' : 'replies'}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Ticket Details */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {selectedTicket.subject}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTicket.status)}`}>
                        {getStatusIcon(selectedTicket.status)}
                        {selectedTicket.status_display}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                        Priority: {selectedTicket.priority_display}
                      </span>
                      <span className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                        {selectedTicket.category_display}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="text-gray-400 hover:text-gray-600 lg:hidden"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Original Message */}
              <div className="p-6 bg-gray-50 border-b border-gray-200">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-medium">
                      {selectedTicket.created_by_name?.charAt(0) || 'U'}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        {selectedTicket.created_by_name}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(selectedTicket.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                    {selectedTicket.attachment && (
                      <a
                        href={selectedTicket.attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center mt-3 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Paperclip className="h-4 w-4 mr-1" />
                        View Attachment
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Responses */}
              <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                  selectedTicket.responses.map((response) => (
                    <div key={response.id} className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full ${
                          response.is_admin_response ? 'bg-green-600' : 'bg-blue-600'
                        } text-white flex items-center justify-center font-medium`}>
                          {response.user_name?.charAt(0) || 'U'}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">
                            {response.user_name}
                            {response.is_admin_response && (
                              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                Support Team
                              </span>
                            )}
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(response.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{response.message}</p>
                        {response.attachment && (
                          <a
                            href={response.attachment}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center mt-2 text-sm text-blue-600 hover:text-blue-800"
                          >
                            <Paperclip className="h-4 w-4 mr-1" />
                            View Attachment
                          </a>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-4">No responses yet</p>
                )}
              </div>

              {/* Reply Box */}
              {selectedTicket.status !== 'closed' && (
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <div className="space-y-3">
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleSendReply(selectedTicket.id)}
                        disabled={sendingReply || !replyMessage.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingReply ? (
                          <>
                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            <span>Send Reply</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {selectedTicket.status === 'closed' && (
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <p className="text-center text-gray-500">
                    This ticket is closed. Contact support if you need to reopen it.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg h-full flex items-center justify-center">
              <div className="text-center text-gray-500">
                <MessageCircle className="h-16 w-16 mx-auto mb-3 text-gray-400" />
                <p>Select a ticket to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportTicketsPanel;
